<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import { useAirBulletin } from '../composables/useAirBulletin';
import { formatReadingTime } from '../api/freshness';
import { IS_DEBUG_MOCK } from '../api/psi';
const { trendPoints, trendSummary, isTrendLoading, hasTrendError, trendPartial, refreshTrend } =
  useAirBulletin();
const id = useId();
const selected = ref<string | null>(null);
const activeIndex = computed(() => {
  const index = trendPoints.value.findIndex((point) => point.timestamp === selected.value);
  return index < 0 ? Math.max(0, trendPoints.value.length - 1) : index;
});
const active = computed(() => trendPoints.value[activeIndex.value]);
const extent = computed(() => {
  const values = trendPoints.value.map((point) => point.psi);
  const min = Math.max(0, Math.min(...values) - 5);
  return { min, max: Math.max(min + 10, ...values) + 5 };
});
const coordinates = computed(() => {
  const latest = trendPoints.value.at(-1);
  if (!latest) return [];
  const end = Date.parse(latest.timestamp);
  return trendPoints.value.map((point) => ({
    ...point,
    x:
      12 +
      ((Date.parse(point.timestamp) - (end - 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000)) * 296,
    y: 72 - ((point.psi - extent.value.min) / (extent.value.max - extent.value.min)) * 60,
  }));
});
const line = computed(() =>
  coordinates.value
    .map((point, index, all) => {
      const gap =
        index > 0 &&
        Date.parse(point.timestamp) - Date.parse(all[index - 1].timestamp) > 90 * 60 * 1000;
      return `${index === 0 || gap ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
    })
    .join(' '),
);
const period = computed(() => {
  const first = trendPoints.value[0];
  const last = trendPoints.value.at(-1);
  return first && last
    ? Math.round((Date.parse(last.timestamp) - Date.parse(first.timestamp)) / 3600000)
    : 0;
});
function inspect(event: Event) {
  const index = Number((event.target as HTMLInputElement).value);
  selected.value = trendPoints.value[index]?.timestamp ?? null;
}
</script>
<template>
  <section class="trend-card" :aria-labelledby="`${id}-title`" :aria-busy="isTrendLoading">
    <div class="trend-heading">
      <h3 :id="`${id}-title`">24-hour PSI trend</h3>
      <span
        v-if="trendSummary.delta !== null"
        class="trend-direction"
        :data-direction="trendSummary.label"
        >{{ trendSummary.label }}</span
      >
    </div>
    <template v-if="trendPoints.length >= 2">
      <p v-if="trendSummary.delta !== null" class="trend-summary">
        {{ trendSummary.delta > 0 ? '+' : '' }}{{ trendSummary.delta }} PSI over {{ period }} hours
        <span>· highest regional reading</span>
      </p>
      <svg
        class="trend-chart"
        viewBox="0 0 320 84"
        role="img"
        :aria-labelledby="`${id}-chart-title ${id}-chart-desc`"
      >
        <title :id="`${id}-chart-title`">
          {{ trendSummary.label }} PSI across the available hourly readings
        </title>
        <desc :id="`${id}-chart-desc`">
          {{ trendPoints.length }} readings, ranging from
          {{ Math.min(...trendPoints.map((p) => p.psi)) }} to
          {{ Math.max(...trendPoints.map((p) => p.psi)) }} PSI. Gaps indicate missing hours. Explore
          exact readings with the slider below.
        </desc>
        <path d="M12,72 L308,72" class="trend-axis" />
        <path :d="line" class="trend-line" />
        <circle
          v-for="point in coordinates"
          :key="point.timestamp"
          :cx="point.x"
          :cy="point.y"
          :r="point.timestamp === active?.timestamp ? 4 : 2"
          class="trend-point"
        />
      </svg>
      <label class="trend-inspection" :for="`${id}-range`"
        ><span>{{ active?.psi }} PSI</span> ·
        {{ formatReadingTime(active?.timestamp ?? null) }}</label
      >
      <input
        :id="`${id}-range`"
        class="trend-scrubber"
        type="range"
        min="0"
        :max="trendPoints.length - 1"
        :value="activeIndex"
        :aria-valuetext="active ? `${active.psi} PSI, ${formatReadingTime(active.timestamp)}` : ''"
        aria-label="Explore hourly PSI readings"
        @input="inspect"
      />
      <p v-if="hasTrendError || trendPartial || trendPoints.length < 24" class="trend-note">
        {{ hasTrendError ? 'History refresh delayed. ' : ''
        }}{{
          trendPartial || trendPoints.length < 24
            ? 'Some hourly readings are unavailable; gaps are left visible.'
            : 'Showing available readings.'
        }}
      </p>
      <details class="trend-history">
        <summary>View hourly readings</summary>
        <div class="trend-table-wrap">
          <table>
            <caption class="sr-only">
              Highest regional 24-hour PSI at each hourly report
            </caption>
            <thead>
              <tr>
                <th scope="col">Reading time (SGT)</th>
                <th scope="col">PSI</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="point in [...trendPoints].reverse()" :key="point.timestamp">
                <td>{{ formatReadingTime(point.timestamp) }}</td>
                <td>{{ point.psi }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </template>
    <p v-else class="trend-note">
      {{
        IS_DEBUG_MOCK
          ? 'Historical data is unavailable in mock preview.'
          : isTrendLoading
            ? 'Loading official hourly readings…'
            : 'Not enough history to show a trend. The current reading is still shown above.'
      }}
    </p>
    <button
      v-if="hasTrendError && !isTrendLoading"
      class="text-button"
      type="button"
      @click="refreshTrend"
    >
      Retry history
    </button>
  </section>
</template>
